'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Bookmark, Share2, Check, Copy } from 'lucide-react';
import { trpc } from '@/lib/trpc/client';
import { useUser } from '@clerk/nextjs';
import { getOrCreateVisitorId } from '@/lib/visitor-id';

interface TravelGuideInteractionsProps {
  guideId: number;
  guideTitle: string;
}

export default function TravelGuideInteractions({ guideId, guideTitle }: TravelGuideInteractionsProps) {
  const { isSignedIn } = useUser();
  const utils = trpc.useUtils();
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  // Get current URL for sharing
  const guideUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Get visitor ID for anonymous users
  const visitorId = !isSignedIn ? getOrCreateVisitorId() : undefined;

  // Check if guide is in wishlist - use travelGuideId parameter
  const { data: wishlistStatus, isLoading } = trpc.wishlist.checkTravelGuide.useQuery(
    { travelGuideId: guideId, visitorId },
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const isInWishlist = wishlistStatus?.isInWishlist || false;

  // Add to wishlist mutation
  const addToWishlist = trpc.wishlist.addTravelGuide.useMutation({
    onSuccess: () => {
      utils.wishlist.checkTravelGuide.invalidate({ travelGuideId: guideId, visitorId });
      utils.wishlist.getAllTravelGuides.invalidate({ visitorId });
      utils.wishlist.getCount.invalidate({ visitorId });
    },
    onError: (error) => {
      if (error.message.includes('CONFLICT') || error.message.includes('already')) {
        alert('This guide is already in your wishlist.');
      } else {
        alert(`Failed to add to wishlist: ${error.message}`);
      }
    },
  });

  // Remove from wishlist mutation
  const removeFromWishlist = trpc.wishlist.removeTravelGuide.useMutation({
    onSuccess: () => {
      utils.wishlist.checkTravelGuide.invalidate({ travelGuideId: guideId, visitorId });
      utils.wishlist.getAllTravelGuides.invalidate({ visitorId });
      utils.wishlist.getCount.invalidate({ visitorId });
    },
    onError: (error) => {
      if (error.message.includes('NOT_FOUND')) {
        alert('This guide is not in your wishlist.');
      } else {
        alert('Failed to remove from wishlist. Please try again.');
      }
    },
  });

  const handleWishlistClick = () => {
    if (isInWishlist) {
      removeFromWishlist.mutate({ travelGuideId: guideId, visitorId });
    } else {
      addToWishlist.mutate({ travelGuideId: guideId, visitorId });
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({
          title: guideTitle,
          text: `Check out this travel guide: ${guideTitle}`,
          url: guideUrl,
        });
        setShowShareMenu(false);
      } catch (error) {
        console.log('Share cancelled or failed:', error);
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(guideUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowShareMenu(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  // Calculate menu position when it opens
  useEffect(() => {
    if (showShareMenu && shareButtonRef.current) {
      const buttonRect = shareButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: buttonRect.bottom + 8, // 8px gap below button
        right: window.innerWidth - buttonRect.right, // Distance from right edge
      });
    }
  }, [showShareMenu]);

  const handleShareClick = () => {
    // If native share is available on mobile, use it directly
    if (typeof navigator !== 'undefined' && 'share' in navigator && typeof window !== 'undefined' && window.innerWidth < 768) {
      handleNativeShare();
    } else {
      // On desktop, show menu
      setShowShareMenu(!showShareMenu);
    }
  };

  const isProcessing = addToWishlist.isPending || removeFromWishlist.isPending;

  return (
    <div className="flex items-center gap-3 sm:gap-4">
      {/* Add to Wishlist Button */}
      <button
        onClick={handleWishlistClick}
        disabled={isProcessing || isLoading}
        className="flex items-center gap-2 text-white hover:text-white/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-target"
        aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <Bookmark 
          className={`h-5 w-5 sm:h-6 sm:w-6 transition-all ${isInWishlist ? 'text-white' : 'text-white/80'} ${isProcessing ? 'animate-pulse' : ''}`}
          fill={isInWishlist ? '#cdc50a' : 'none'}
          strokeWidth={2}
        />
        <span className="text-sm sm:text-base font-medium text-white drop-shadow-md">
          {isInWishlist ? 'Saved for Later' : 'Read Later'}
        </span>
      </button>
      
      {/* Share Button */}
      <div className="relative">
        <button
          ref={shareButtonRef}
          onClick={handleShareClick}
          className="flex items-center gap-2 text-white hover:text-white/80 transition-all touch-target"
          aria-label="Share guide"
        >
          <Share2 className="h-5 w-5 sm:h-6 sm:w-6 text-white drop-shadow-md" strokeWidth={2} />
          <span className="text-sm sm:text-base font-medium text-white drop-shadow-md">Share</span>
        </button>

        {/* Share Menu - Using fixed positioning to avoid overflow issues */}
        {showShareMenu && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setShowShareMenu(false)}
            />
            
            {/* Menu - Fixed positioning relative to viewport */}
            <div 
              className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px]"
              style={{
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
              }}
            >
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase">Share this guide</p>
              </div>
              
              {/* Native Share (desktop only since mobile uses it directly) */}
              {typeof navigator !== 'undefined' && 'share' in navigator && typeof window !== 'undefined' && window.innerWidth >= 768 && (
                <button
                  onClick={handleNativeShare}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 text-sm"
                >
                  <Share2 className="h-4 w-4 text-gray-600" />
                  <span className="text-gray-700">Share via...</span>
                </button>
              )}
              
              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 text-sm"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-green-600 font-medium">Link copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 text-gray-600" />
                    <span className="text-gray-700">Copy link</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

